import { Component, Element, h, Prop, Watch, Event, EventEmitter, Host, Listen, Method } from '@stencil/core';
import { focusElement, runAfterNextFrameRender, waitForCSSTransitionToFinish } from '../../utils/functions';
import { getInternalFormat, getInternalFormatFromDate, getDateFromInternalFormat, isDateString, formatDate, isValidDate } from '../../utils/dates';
import { SomCalendar } from '../som-calendar/som-calendar';
import { KEY_CODES } from '../../utils/variables';

@Component({
  tag: 'som-date-picker-mobile',
  styleUrl: 'som-date-picker-mobile.scss',
})
export class SomDatePickerMobile {
  focusedElement: HTMLElement = null;

  containerEl: HTMLDivElement;

  contentContainerEl: HTMLDivElement;

  calendarContainerEl: HTMLDivElement;

  input1El: HTMLInputElement;

  input2El: HTMLInputElement;

  datePickerEl: HTMLSomDatePickerElement;

  dateRangePickerEl: HTMLSomDateRangePickerElement;

  lastBodyOverflowStyle: string;

  lastBodyMSOverflowStyle: string;

  tmp: {
    date1?: string;
    date2?: string;
    value1?: string;
    value2?: string;
  } = {};

  @Element() el: HTMLElement;

  @Prop({ mutable: true, reflect: true }) value1 = '';

  @Prop({ mutable: true, reflect: true }) value2 = '';

  @Prop() focusInputOnShow = false;

  @Prop() date1 = '';

  @Prop() date2 = '';

  @Prop() enableRangePicker = false;

  @Prop({ mutable: true, reflect: true }) show = false;

  @Watch('show')
  handleShowChange() {
    let callbackAfterCSSTransition = () => {};
    if (this.show) {
      // Save current body overflow style and disable overflow (thus scrolling) on body.
      const bodyEl = document.querySelector('body');
      this.lastBodyOverflowStyle = bodyEl.style.getPropertyValue('overflow');
      bodyEl.style.overflow = 'hidden';
      this.lastBodyMSOverflowStyle = bodyEl.style['-ms-overflow-style'];
      bodyEl.style['-ms-overflow-style'] = 'none';

      this.containerEl.style.display = 'block';

      runAfterNextFrameRender(() => {
        // Scroll content to the top
        this.contentContainerEl.style.bottom = '0';
      });

      callbackAfterCSSTransition = () => {
        if (this.focusInputOnShow) {
          focusElement(this.input1El);
        } else if (this.containerEl) {
          focusElement(this.containerEl);
        }
      };
    } else {
      this.contentContainerEl.style.bottom = '-100%';

      this.undoBodyStyling();

      callbackAfterCSSTransition = () => {
        this.containerEl.style.display = 'none';
      };
    }

    waitForCSSTransitionToFinish(this.contentContainerEl, callbackAfterCSSTransition);
  }

  @Prop() format: 'MM-DD-YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'YYYY/MM/DD' = 'MM/DD/YYYY';

  @Event() datesChanged: EventEmitter;

  @Event() close: EventEmitter;

  @Listen('keydown')
  async handleKeyDown(e) {
    if (this.show) {
      switch (e.keyCode) {
        case KEY_CODES.ESC:
          this.handleClose();
          break;
        case KEY_CODES.ARROW_DOWN:
          e.preventDefault();
          this.setDatePickerFocus();
          break;
        case KEY_CODES.TAB:
          {
            e.preventDefault();
            e.stopPropagation();
            const focusableElements = this.getFocusableElements();
            const focusedElementIndex = focusableElements.findIndex(element => element === document.activeElement);

            if (focusableElements.length > 0) {
              // Popover is shown, let's capture the tab
              let newIndex = 0;
              if (e.shiftKey) {
                if (focusedElementIndex <= 0) {
                  newIndex = focusableElements.length - 1;
                } else {
                  newIndex = focusedElementIndex - 1;
                }
              } else {
                newIndex = (focusedElementIndex + 1) % focusableElements.length;
              }

              this.focusedElement = focusableElements[newIndex];
              focusElement(this.focusedElement);
            }
          }
          break;
        default:
          break;
      }
    }
  }

  // Resets the date picker to its initial state
  @Method() async reset() {
    if (this.dateRangePickerEl) {
      this.dateRangePickerEl.setAttribute('start-date', this.date1);
      this.dateRangePickerEl.setAttribute('end-date', this.date2);
    } else if (this.datePickerEl) {
      this.datePickerEl.setAttribute('date', this.date1);
    }

    runAfterNextFrameRender(() => {
      (this.dateRangePickerEl || this.datePickerEl).reset();
    });

    // Reset inputs
    this.input1El.value = this.date1 && isValidDate(this.date1) ? formatDate(getDateFromInternalFormat(this.date1), this.format) : '';
    if (this.input2El) {
      this.input2El.value = this.date2 && isValidDate(this.date2) ? formatDate(getDateFromInternalFormat(this.date2), this.format) : '';
    }

    return Promise.resolve();
  }

  disconnectedCallback() {
    this.undoBodyStyling();
  }

  undoBodyStyling() {
    const bodyEl = document.querySelector('body');
    bodyEl.style.overflow = this.lastBodyOverflowStyle || '';
    bodyEl.style['-ms-overflow-style'] = this.lastBodyMSOverflowStyle || '';
  }

  setDatePickerFocus() {
    const selector = this.enableRangePicker ? 'som-date-range-picker' : 'som-date-picker';
    const elemToFocus = this.el.querySelector(selector);
    if (elemToFocus) {
      focusElement(elemToFocus);
    }
    return Promise.resolve();
  }

  getFocusableElements() {
    return [].slice.call(this.el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
  }

  handleClose() {
    this.show = false;
    this.close.emit();
  }

  // Date range picker change handler
  handleDateRangeChange(e) {
    e.stopPropagation();

    const {
      startDate,
      endDate,
    }: {
      startDate: Date;
      endDate: Date;
    } = e.detail;

    this.tmp.value1 = formatDate(startDate, this.format);
    this.tmp.date1 = getInternalFormatFromDate(startDate);

    if (endDate) {
      this.tmp.value2 = formatDate(endDate, this.format);
      this.tmp.date2 = getInternalFormatFromDate(endDate);
    } else {
      this.tmp.value2 = '';
      this.tmp.date2 = '';
    }

    this.input1El.value = this.tmp.value1;
    if (this.input2El) {
      this.input2El.value = this.tmp.value2;
    }
  }

  // Date picker change handler
  handleDateChange(e) {
    e.stopPropagation();

    const date: Date = e.detail;

    this.tmp.value1 = formatDate(date, this.format);
    this.tmp.date1 = getInternalFormatFromDate(date);

    this.input1El.value = this.tmp.value1;
  }

  handleInput1Text(e) {
    this.tmp.value1 = e.target.value;
    this.tmp.date1 = isDateString(e.target.value, this.format) ? getInternalFormat(e.target.value, this.format) : '';

    if (this.dateRangePickerEl) {
      this.dateRangePickerEl.setAttribute('start-date', this.tmp.date1);
    } else if (this.datePickerEl) {
      this.datePickerEl.setAttribute('date', this.tmp.date1);
    }
  }

  handleInput2Text(e) {
    this.tmp.value2 = e.target.value;
    this.tmp.date2 = isDateString(e.target.value, this.format) ? getInternalFormat(e.target.value, this.format) : '';

    if (this.dateRangePickerEl) {
      this.dateRangePickerEl.setAttribute('end-date', this.tmp.date2);
    }
  }

  handleClear() {
    this.tmp.date1 = '';
    this.tmp.value1 = '';
    this.tmp.date2 = '';
    this.tmp.value2 = '';

    this.input1El.value = '';
    if (this.input2El) {
      this.input2El.value = '';
    }

    if (this.dateRangePickerEl) {
      this.dateRangePickerEl.setAttribute('start-date', '');
      this.dateRangePickerEl.setAttribute('end-date', '');
    } else if (this.datePickerEl) {
      this.datePickerEl.setAttribute('date', '');
    }
  }

  handleApply() {
    this.datesChanged.emit({
      value1: this.tmp.value1,
      value2: this.tmp.value2,
      date1: this.tmp.date1 ? getDateFromInternalFormat(this.tmp.date1) : '',
      date2: this.tmp.date2 ? getDateFromInternalFormat(this.tmp.date2) : '',
    });
  }

  render() {
    return (
      <Host>
        <div
          ref={ref => {
            this.containerEl = ref;
          }}
          class="som-dpm"
          tabindex="-1"
        >
          <div
            ref={ref => {
              this.contentContainerEl = ref;
            }}
            class="som-dpm-container"
          >
            <div class="som-dpm-header">
              <div class="som-dpm-close-button-container">
                <a href="#" class="som-dpm-close-button" onClick={this.handleClose.bind(this)} tabindex="0" role="button" aria-label="Close">
                  <som-icon name="close" size="md"></som-icon>
                  <span class="visually-hidden">Close</span>
                </a>
              </div>
              <div class="som-dpm-input-container" style={{ width: `${SomCalendar.WIDTH}px` }}>
                <input
                  ref={ref => {
                    this.input1El = ref;
                  }}
                  type="text"
                  class="form-control"
                  value={this.value1}
                  onInput={this.handleInput1Text.bind(this)}
                  placeholder={this.format}
                  aria-label={this.enableRangePicker ? 'Start date' : 'Date'}
                />

                {this.enableRangePicker
                  ? [
                      <div class="som-dpm-input-arrow">
                        <som-icon name="caret-right" color="black" size="sm"></som-icon>
                      </div>,
                      <input
                        ref={ref => {
                          this.input2El = ref;
                        }}
                        type="text"
                        class="form-control"
                        value={this.value2}
                        onInput={this.handleInput2Text.bind(this)}
                        placeholder={this.format}
                        aria-label="End date"
                      />,
                    ]
                  : null}
              </div>
            </div>
            <div
              ref={ref => {
                this.calendarContainerEl = ref;
              }}
              class="som-dpm-calendar"
            >
              <div class="som-dpm-calendar-content">
                {this.enableRangePicker ? (
                  <som-date-range-picker
                    ref={ref => {
                      this.dateRangePickerEl = ref;
                    }}
                    onDatesChanged={this.handleDateRangeChange.bind(this)}
                    startDate={this.date1}
                    endDate={this.date2}
                    vertical={true}
                  />
                ) : (
                  <som-date-picker
                    ref={ref => {
                      this.datePickerEl = ref;
                    }}
                    onDateChange={this.handleDateChange.bind(this)}
                    date={this.date1}
                    vertical={true}
                  />
                )}
              </div>
            </div>
            <div class="som-dpm-actions">
              <som-button size="sm" onClick={this.handleClear.bind(this)}>
                Clear
              </som-button>
              <som-button size="sm" onClick={this.handleApply.bind(this)}>
                Apply
              </som-button>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
